import React from "react"
import LoanOptionCard from "./loan-option-card"
import IncentiveLoanCard from "./incentive-loan-card"
import './loan-options.scss'
import {
  dollars,
  LoanOption,
  IProduct,
  LoanCalculations,
  Incentive,
  dollarsToNum,
  APR_EXPLANATION_LINK,
  ITenant,
  LoanOptions as LoanOptionsShared,
} from "@oneethos/shared"

type LoanOptionsProps = {
  loanOptions: LoanOption[]
  formData: any
  financingTenant?: ITenant
  isConsumerView?: boolean
  onProductSelect?: (product: IProduct, isIncentiveVersion?: boolean) => void,
  maxPaymentAmount?: number
  label?: string
  incentive?: Incentive
  showSavings?: boolean
}

export const LoanOptions = ({
  loanOptions,
  formData,
  financingTenant,
  isConsumerView,
  onProductSelect,
  label,
  incentive: propIncentive,
  showSavings = false
}: LoanOptionsProps) => {

  const {
    projectType,
    incentive: formDataIncentive
  } = formData

  const isResidential = projectType === "residential"
  const statedGrossAnnualIncome = dollarsToNum(formData.statedGrossAnnualIncome)
  const coStatedGrossAnnualIncome = formData.isJointApp
    ? dollarsToNum(formData.coStatedGrossAnnualIncome)
    : undefined
  const incentive = propIncentive || formDataIncentive
  const incentiveProducts = incentive?.incentiveProducts || []

  const {
    withIncentives,
    standard
  } = LoanOptionsShared.groupLoanOptionsByIncentive(
    loanOptions,
    incentiveProducts,
    statedGrossAnnualIncome,
    coStatedGrossAnnualIncome
  )

  return (
    <div className="fin-tenant">
      {label && <p>{label}</p>}
      {withIncentives.length > 0 && (
        <div className={`d-flex gap-4 loan-options has-incentive mb-4`}>
          {withIncentives.map(({ product, matchingIncentive }) => {
            const isSelected = product.id === formData.productId && formData.incentiveSelected === true
            return (
              <IncentiveLoanCard
                key={`incentive-${product.id}`}
                financingTenant={financingTenant}
                financingPartnerLogo={incentive.financingPartnerLogo}
                incentiveProduct={matchingIncentive}
                isConsumerView={isConsumerView}
                isSelected={isSelected}
                getProduct={() => onProductSelect(product, true)}
                showSavings={showSavings}
              />
            )
          })}
          {isConsumerView &&
            <span className="fst-italic">
              *Auto-pay is required to qualify for loans in partnership with Montgomery County Green Bank.
            </span>}
        </div>
      )}
      <div className={`d-flex gap-4 loan-options`}>
        {standard.map(({ product, payments, apr }) => {
          const hasIncentiveVersion = withIncentives.some(opt => opt.product?.id === product?.id)
          const isSelected = product.id === formData.productId && (
            !hasIncentiveVersion || formData.incentiveSelected === false)
          return (
            <LoanOptionCard
              key={product.id}
              financingTenant={financingTenant}
              loanTermYears={LoanCalculations.calculateLoanTermInYears(product.term)}
              loanTermMonths={product.term}
              interestWithAutoPay={product.interestRateWithAutopay}
              estimatedPaymentWithAutopay={dollars(payments.withAutoPay)}
              estimatedPaymentWithOutAutopay={dollars(payments.withoutAutoPay)}
              isConsumerView={isConsumerView}
              isSelected={isSelected}
              getProduct={() => onProductSelect(product, false)}
              interestOnlyPeriod={product.interestOnlyPeriod}
              apr={apr}
              isResidential={isResidential}
            />
          )
        })}
      </div>

      {projectType === "residential" && (
        <p className="mt-3 fst-italic">
          *{financingTenant.name} offers a discount to clients that opt into, and maintain,
          automatic monthly payments. The bill amount will automatically be withdrawn
          from the client's checking account.
        </p>
      )}
      <a
        href={APR_EXPLANATION_LINK}
        className="text-small d-flex mb-3 fst-italic"
        target="_blank"
      >**How is APR different from interest rate?</a>
      {incentive && !isConsumerView && <span className="fst-italic">
        ***Estimated savings are based on savings on finance charges compared to loan products with the same term.
      </span>}
    </div>
  )
}

export default LoanOptions